/**
 * ErpArrivalsLine: the month's count on the client's home, "31 invoices
 * arrived from Busy this month · 2 need a fix" (lane erpin, §8.4). The count
 * that needs a fix opens Settings, Your ERP, where each one carries its
 * sentence and its button. Says nothing when no ERP sends documents here.
 *
 * @since 1.0.0 (erpin-2)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Plug } from 'lucide-react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/shared/hooks/useAuth';
import { stillNeedFix, type ArrivalFacts } from '@/shared/documents/outcomes';

interface Counts { name: string; arrived: number; needFix: number }

export function ErpArrivalsLine({ className = '' }: { className?: string }) {
    const navigate = useNavigate();
    const orgId = useAuth((s) => s.currentOrg?.id);
    const [read, setRead] = useState<{ org: string; counts: Counts | null } | null>(null);

    useEffect(() => {
        if (!orgId) return;
        let cancelled = false;
        void (async () => {
            try {
                const conn = await supabase.from('erp_connections').select('name').eq('org_id', orgId)
                    .order('created_at').limit(1).maybeSingle();
                // No table yet (erpin-1 not applied) or no ERP: nothing to say.
                if (conn.error || !conn.data) { if (!cancelled) setRead({ org: orgId, counts: null }); return; }
                const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
                const arr = await supabase.from('document_arrivals')
                    .select('received_at, outcome, seller_gstin, fy, doc_type, doc_no, resolved_at')
                    .eq('org_id', orgId).eq('channel', 'api').neq('action', 'dry_run')
                    .gte('received_at', start.toISOString()).limit(5000);
                if (cancelled) return;
                if (arr.error) { setRead({ org: orgId, counts: null }); return; }
                const rows = (arr.data ?? []) as ArrivalFacts[];
                setRead({
                    org: orgId,
                    counts: {
                        name: String((conn.data as { name?: string }).name ?? '').trim() || 'your ERP',
                        arrived: rows.length,
                        needFix: stillNeedFix(rows).length,
                    },
                });
            } catch {
                // A line that cannot be read is a line not shown; the home goes on.
                if (!cancelled) setRead({ org: orgId, counts: null });
            }
        })();
        return () => { cancelled = true; };
    }, [orgId]);

    const c = read && read.org === orgId ? read.counts : null;
    if (!c || c.arrived === 0) return null;

    return (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 ${className}`}>
            <Plug size={15} className="text-teal-600 shrink-0" />
            <span>{c.arrived} document{c.arrived === 1 ? '' : 's'} arrived from {c.name} this month</span>
            {c.needFix > 0 && (
                <button type="button" onClick={() => void navigate({ to: '/settings', hash: 'erp' })}
                    className="inline-flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400 hover:underline">
                    · {c.needFix} need{c.needFix === 1 ? 's' : ''} a fix <ArrowRight size={13} />
                </button>
            )}
        </div>
    );
}
